import axios from 'axios';

function getApiUrl() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'http://localhost:8080/api';
  if (hostname.includes('dev.shramsena.in') || hostname.includes('-development'))
    return 'https://shramsena-platform-development.up.railway.app/api';
  return 'https://shramsena-platform-production.up.railway.app/api';
}

const API = axios.create({
  baseURL: getApiUrl(),
  headers: { 'Content-Type': 'application/json' },
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const onAuthPage = ['/login', '/company/login', '/register', '/company/register']
      .includes(window.location.pathname);
    if (error.response?.status === 401 && !onAuthPage) {
      // Determine redirect based on stored user role
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      const isCompany = user?.role === 'ROLE_COMPANY' || user?.role === 'COMPANY';
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = isCompany ? '/company/login' : '/login';
    }
    return Promise.reject(error);
  }
);

// ── AUTH ──────────────────────────────────────────────────────────────
export const authAPI = {
  sendOtp: (phone) => API.post('/auth/otp/send', { phone }),
  verifyOtp: (phone, otp) => API.post('/auth/otp/verify', { phone, otp }),
  register: (data) => API.post('/auth/register', data),
  login: (phone, password) => API.post('/auth/login', { phone, password }),
  refreshToken: (refreshToken) => API.post('/auth/refresh', { refreshToken }),
  getDomains: () => API.get('/auth/domains'),
  getStaffTypes: (domainId) => API.get(`/auth/domains/${domainId}/staff-types`),
  health: () => API.get('/auth/health'),
  sendPasswordResetOtp: (phone) =>
    API.post('/auth/forgot-password/send-otp', { phone }),
  resetPassword: (phone, otp, newPassword) =>
    API.post('/auth/forgot-password/reset', { phone, otp, newPassword }),
};

// ── STAFF ─────────────────────────────────────────────────────────────
export const staffAPI = {
  getProfile: () => API.get('/staff/profile'),
  updateProfile: (data) => API.put('/staff/profile', data),
  uploadDocuments: (formData) => API.post('/staff/upload-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  completeRegistration: (data) => API.post('/staff/complete-registration', data || {}),
  getMyReferrals: () => API.get('/staff/referrals'),
  getMyOpportunities: () => API.get('/staff/hire-requests'),
};

// ── ADMIN ─────────────────────────────────────────────────────────────
export const adminAPI = {
  getPendingStaff: (page = 0, size = 20) =>
    API.get(`/admin/pending-staff?page=${page}&size=${size}`),
  getAllStaff: (status, page = 0, size = 20) =>
    API.get('/admin/staff', { params: { status, page, size } }),
  approveRegistration: (id) => API.post(`/admin/staff/${id}/approve`),
  rejectRegistration: (id, reason) =>
    API.post(`/admin/staff/${id}/reject`, { reason }),
  getPendingReferrals: () => API.get('/admin/pending-referrals'),
  approveReferral: (id, remarks) =>
    API.post(`/admin/referral/${id}/approve`, { remarks }),
  rejectReferral: (id, reason) =>
    API.post(`/admin/referral/${id}/reject`, { reason }),
  getPendingIncentives: () => API.get('/admin/pending-incentives'),
  markIncentivePaid: (id, data) => API.post(`/admin/incentive/${id}/pay`, data),
  getDashboardStats: () => API.get('/admin/dashboard/stats'),
  getStaffDetails: (id) => API.get(`/admin/staff/${id}/details`),
};

// ── COMPANY ───────────────────────────────────────────────────────────
export const companyAPI = {
  register: (data) => API.post('/company/register', data),
  login: (phone, password) => API.post('/company/login', { phone, password }),
  searchStaff: (params) => API.get('/staff/search', { params }),
  addShortlist: (staffId) => API.post(`/shortlist/${staffId}`),
  removeShortlist: (staffId) => API.delete(`/shortlist/${staffId}`),
  getShortlist: () => API.get('/shortlist'),
  createHireRequest: (data) => API.post('/hire-requests', data),
  withdrawHireRequest: (id) => API.delete(`/hire-requests/${id}`),
  getMyHireRequests: () => API.get('/hire-requests/mine'),
  getProfile: () => API.get('/company/profile'),
  updateProfile: (data) => API.put('/company/profile', data),
};

// ── ADMIN — COMPANY MANAGEMENT ────────────────────────────────────────
export const adminCompanyAPI = {
  getCompany: (id) => API.get(`/admin/companies/${id}`),
  getCompanies: (status, page = 0, size = 20) =>
    API.get('/admin/companies', { params: { status, page, size } }),
  approveCompany: (id) => API.put(`/admin/companies/${id}/approve`),
  rejectCompany: (id, note) =>
    API.put(`/admin/companies/${id}/reject`, { note }),
  getHireRequests: (status, page = 0, size = 20) =>
    API.get('/admin/hire-requests', { params: { status, page, size } }),
  approveHireRequest: (id) => API.put(`/admin/hire-requests/${id}/approve`),
  rejectHireRequest: (id, note) =>
    API.put(`/admin/hire-requests/${id}/reject`, { note }),
};


// ── COMPANY — FACILITIES ──────────────────────────────────────────────
export const facilityAPI = {
  list: () => API.get('/company/facilities'),
  add: (data) => API.post('/company/facilities', data),
  update: (id, data) => API.put(`/company/facilities/${id}`, data),
  remove: (id) => API.delete(`/company/facilities/${id}`),
};

// ── COMPANY — STAFFING PLANS ──────────────────────────────────────────
export const staffingPlanAPI = {
  generate: (data) => API.post('/company/staffing-plans/generate', data),
  list: () => API.get('/company/staffing-plans'),
  get: (id) => API.get(`/company/staffing-plans/${id}`),
};


// ── COMPANY — OPPORTUNITIES ───────────────────────────────────────
export const companyOpportunityAPI = {
  list: () => API.get('/company/opportunities'),
  create: (data) => API.post('/company/opportunities', data),
  update: (id, data) => API.put(`/company/opportunities/${id}`, data),
  close: (id) => API.patch(`/company/opportunities/${id}/close`),
  getApplicants: (id) => API.get(`/company/opportunities/${id}/applicants`),
  shortlist: (appId) => API.patch(`/company/opportunities/applicants/${appId}/shortlist`),
  selectApplicant: (appId) => API.post(`/company/opportunities/applicants/${appId}/select`),
};

// ── STAFF — OPPORTUNITIES ─────────────────────────────────────────
export const staffOpportunityAPI = {
  browse: (page = 0, size = 10) => API.get(`/staff/opportunities?page=${page}&size=${size}`),
  apply: (data) => API.post('/staff/opportunities/apply', data),
  withdraw: (appId) => API.patch(`/staff/opportunities/applications/${appId}/withdraw`),
  myApplications: () => API.get('/staff/opportunities/applications'),
};

// ── PLACEMENTS ────────────────────────────────────────────────────
export const placementAPI = {
  // Staff
  respondToHireRequest: (id, data) => API.post(`/staff/hire-requests/${id}/respond`, data),
  myPlacements: () => API.get('/staff/placements'),
  rateCompany: (id, data) => API.post(`/staff/placements/${id}/rate-company`, data),
  // Company
  companyPlacements: () => API.get('/company/placements'),
  releaseStaff: (id, data) => API.post(`/company/placements/${id}/release`, data),
  // Admin
  activePlacements: () => API.get('/admin/placements/active'),
  confirmPlacement: (hireRequestId) => API.post(`/admin/hire-requests/${hireRequestId}/confirm-placement`),
};

export default API;